import React, { useState } from "react";
import {
  Box,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Chip,
  Pagination,
  Typography,
} from "@mui/material";
import { formatDuration, formatTimestamp } from "../../utils/constant";
import { TablePagination } from "@mui/material";
import { Skeleton } from "@mui/material";
import { useParams, useNavigate } from "react-router-dom";

const RecentApiTableSkeletonRows = ({ rows = 5 }) => (
  <>
    {Array.from({ length: rows }).map((_, index) => (
      <TableRow key={index}>
        <TableCell><Skeleton width={120} /></TableCell>
        <TableCell><Skeleton width={100} /></TableCell>
        <TableCell><Skeleton width={140} /></TableCell>
        <TableCell>
          <Skeleton variant="rounded" width={50} height={24} />
        </TableCell>
        <TableCell><Skeleton width={80} /></TableCell>
        <TableCell><Skeleton width={40} /></TableCell>
        <TableCell><Skeleton width={60} /></TableCell>
      </TableRow>
    ))}
  </>
);

export const RecentApiCallTable = ({
  tableLoading = false,
  data = [],
  totalCount = 0,
  page,
  rowsPerPage,
  onPageChange,
  onRowsPerPageChange,
  onApiNameClick,
  apiNameFilter,
}) => {
  const { companyName } = useParams();
  const navigate = useNavigate();
  const decodedCompanyName = companyName ? decodeURIComponent(companyName) : "";

  const handleViewLogs = (e, timestamp) => {
    const parsedDate = new Date(timestamp);
    if (Number.isNaN(parsedDate.getTime())) return;

    const date = parsedDate.toISOString().split("T")[0];
    navigate(`/dashboard/${encodeURIComponent(decodedCompanyName)}/view-logs?date=${date}`);
  };

  return (
    <Box>
      <Paper sx={{ width: "100%", borderRadius: 3, boxShadow: 2, p: 2 }}>
         <Box
          sx={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            mb: 2,
          }}
        >
          {/* Left side: Title + subtitle */}
          <Box>
            <Typography variant="h4" fontWeight="bold" gutterBottom>
              API Logs
              {apiNameFilter && (
                <Chip
                  label={apiNameFilter}
                  size="small"
                  onDelete={() => onApiNameClick?.(null)}
                  sx={{ ml: 1, fontSize: "12px", fontWeight: 600 }}
                />
              )}
            </Typography>

            <Typography variant="body2" color="text.secondary" 
              sx={{
                mb: 2,
                fontSize: "1.4rem",
                fontWeight: 400, 
              }}>
              Latest transactions from this customer
            </Typography>
          </Box>
        </Box>
        <TableContainer>
          <Table>
            <TableHead>
              <TableRow>
                <TableCell sx={{mb: 2, fontSize: "14px", fontWeight: 600, color: "#64748B" }}>API</TableCell>
                <TableCell sx={{mb: 2, fontSize: "14px", fontWeight: 600, color: "#64748B" }}>Category</TableCell>
                <TableCell sx={{mb: 2, fontSize: "14px", fontWeight: 600, color: "#64748B" }}>Timestamp</TableCell>
                <TableCell sx={{mb: 2, fontSize: "14px", fontWeight: 600, color: "#64748B" }}>Status</TableCell>
                <TableCell sx={{mb: 2, fontSize: "14px", fontWeight: 600, color: "#64748B" }}>Response Time</TableCell>
                <TableCell sx={{mb: 2, fontSize: "14px", fontWeight: 600, color: "#64748B" }}>Credits</TableCell>
                {/* <TableCell sx={{mb: 2, fontSize: "14px", fontWeight: 600, color: "#64748B" }}>Usage</TableCell> */}
              </TableRow>
            </TableHead>

            <TableBody>
              {tableLoading ? (
                <RecentApiTableSkeletonRows rows={rowsPerPage} />
              ) : data.length > 0 ? (
                data.map((row, index) => (
                <TableRow key={index}>
                  <TableCell sx={{ fontSize: "14px", fontWeight: 400 }}>
                    <Typography
                      // onClick={() => {
                      //   if (!row.apiName) return;
                      //   onApiNameClick?.(row.apiName);
                      // }}
                      onClick={(e) => handleViewLogs(e, row.timestamp)}
                      sx={{
                        color: "#2563EB",
                        fontWeight: 500,
                        fontSize: "14px",
                        cursor: row.apiName ? "pointer" : "default",
                        "&:hover": row.apiName ? { textDecoration: "underline" } : undefined,
                      }}
                    >
                      {row.apiName}
                    </Typography>
                  </TableCell>

                  <TableCell sx={{ fontSize: "14px" }}>{row.category}</TableCell>

                  <TableCell sx={{ fontSize: "14px" }}>{formatTimestamp(row.timestamp)}</TableCell>

                  <TableCell sx={{ fontSize: "14px" }}>
                    <Chip
                      label={row.statusCode}
                      sx={{
                        bgcolor: row.statusCode === 200 ? "#e6f4ea" : "#fdecea",
                        color: row.statusCode === 200 ? "#2e7d32" : "#d32f2f",
                        fontSize: "13px",
                        fontWeight: 600,
                        textTransform: "capitalize",
                      }}
                    />
                  </TableCell>

                  <TableCell sx={{ fontSize: "14px" }}>{formatDuration(row.responseTime)}</TableCell>
                  <TableCell sx={{ fontSize: "14px" }}>{row.creditsConsumed}</TableCell>
                  {/* <TableCell sx={{ fontSize: "14px" }}>
                    <Typography
                      onClick={(e) => handleViewLogs(e, row.timestamp)}
                      sx={{
                        color: "#2563EB",
                        fontWeight: 500,
                        fontSize: "14px",
                        cursor: "pointer",
                        "&:hover": { textDecoration: "underline" },
                      }}
                    >
                      View Logs
                    </Typography>
                  </TableCell> */}
                </TableRow>
              ))
            ) : (
              <TableRow>
                <TableCell colSpan={7} align="center" sx={{ fontSize: "15px" }}>
                  No Recent API Call found
                </TableCell>
              </TableRow>
            )}
            </TableBody>
          </Table>
        </TableContainer>

        {/* Pagination */}
        <TablePagination
          component="div"
          count={totalCount}
          page={page - 1} 
          onPageChange={(e, newPage) => onPageChange(newPage + 1)}
          rowsPerPage={rowsPerPage}
          onRowsPerPageChange={(e) => {
            onPageChange(1); 
            onRowsPerPageChange(Number(e.target.value));
          }}
          rowsPerPageOptions={[15, 25, 50, 100]}
          sx={{
            mt: 2,
            "& .MuiTablePagination-selectLabel, & .MuiTablePagination-displayedRows": {
              fontSize: "12px",
              fontWeight: 600,
            },
            "& .MuiTablePagination-select": {
              fontSize: "12px",
            },
          }}
        />
      </Paper>
    </Box>
  );
};
